import { useSyncExternalStore } from "react";
import { MOBILE_QUERY } from "@trackmark/tokens";

/**
 * Whether the viewport is the mobile one (design/README.md §13: below 900px).
 *
 * Measured on the VIEWPORT, never on the user agent — the same rule §8's add-then-queue decision
 * follows. A narrow window on a desktop is a narrow window, and a phone held sideways is not one.
 *
 * `useSyncExternalStore` rather than an effect and a state: it subscribes to the media query
 * itself, so rotating a phone or dragging a window across the line re-renders. An effect would
 * read the value once after mount, which is how a rotated phone ends up on the wrong design.
 */

const subscribe = (onChange: () => void) => {
  if (typeof globalThis.matchMedia !== "function") return () => {};
  const query = globalThis.matchMedia(MOBILE_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
};

const read = () => typeof globalThis.matchMedia === "function" && globalThis.matchMedia(MOBILE_QUERY).matches;

// The server has no viewport. Desktop is the honest default: it is the design that assumes a
// keyboard, and rendering the mobile one into a desktop window would be the visible mistake.
const readServer = () => false;

export function useMobile(): boolean {
  return useSyncExternalStore(subscribe, read, readServer);
}
